import {
  ActionIcon,
  type ActionIconProps,
  type ButtonProps,
  Group,
  Modal,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { memo, type MouseEvent, type ReactNode, useMemo } from "react";
import { useTheme } from "src/hooks";

import { Button } from "../Button/Button";

interface Props {
  modalText: string | ReactNode;
  onConfirm: () => void | Promise<void>;
  actionIcon?: boolean;
  buttonProps?: ButtonProps;
  actionIconProps?: ActionIconProps & { component?: any };
  children: ReactNode;
  loading?: boolean;
  zIndex?: number;
}

const ConfirmationButton = ({
  modalText,
  onConfirm,
  children,
  loading,
  actionIcon,
  buttonProps,
  actionIconProps,
  zIndex,
}: Props) => {
  const theme = useTheme();
  const [opened, { open, close }] = useDisclosure(false);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    open();
  };

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    close();
    await onConfirm();
  };

  const renderComponent = () => {
    if (actionIcon) {
      return (
        <ActionIcon onClick={handleOpen} {...actionIconProps}>
          {children}
        </ActionIcon>
      );
    }
    return (
      <Button onClick={handleOpen} {...buttonProps}>
        {children}
      </Button>
    );
  };
  const modalSize = useMemo(
    () =>
      typeof modalText === "string" && modalText.length > 50 ? "lg" : "md",
    [modalText],
  );
  return (
    <>
      {renderComponent()}

      <Modal
        opened={opened}
        onClose={close}
        title="Confirmation Required"
        size={modalSize}
        zIndex={zIndex}>
        <Text>{modalText}</Text>
        <Group justify="right" mt={10}>
          <Button variant="filled" loading={loading} onClick={handleConfirm}>
            Confirm
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default memo(ConfirmationButton);
